import { useState } from 'react';

const HomePage = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [formStatus, setFormStatus] = useState('');
  // Fixed generic type syntax by using TypeScript syntax directly
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});
  const [showGeneralError, setShowGeneralError] = useState(false);
  const [submitAttempts, setSubmitAttempts] = useState(0);

  const validateForm = (formData: FormData) => {
    const errors: { [key: string]: string } = {};

    const fullName = formData.get('fullName') as string;
    const email = formData.get('email') as string;

    if (!fullName || fullName.trim() === '') {
      errors.fullName = 'Full Name is required';
    }

    if (!email || email.trim() === '') {
      errors.email = 'Email Address is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errors.email = 'Please enter a valid email address';
    }

    return errors;
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleHomeClick = () => {
    if (window.REACT_APP_NAVIGATE) {
      window.REACT_APP_NAVIGATE('/');
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormErrors({});
    setShowGeneralError(false);

    const form = e.currentTarget;
    const formData = new FormData(form);

    // Validate form
    const errors = validateForm(formData);
    const message = formData.get('message') as string;

    // Check if required fields are missing
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      setShowGeneralError(true);
      setSubmitAttempts(prev => prev + 1);
      return;
    }

    // Check if message is empty and this is the first submit attempt
    if ((!message || message.trim() === '') && submitAttempts === 0) {
      setShowGeneralError(true);
      setSubmitAttempts(prev => prev + 1);
      return;
    }

    setFormStatus('submitting');

    // Convert FormData to URLSearchParams for application/x-www-form-urlencoded
    const params = new URLSearchParams();
    formData.forEach((value, key) => {
      params.append(key, value.toString());
    });

    try {
      const response = await fetch('https://readdy.ai/api/form/d3q6si3tpgsu3r1tnq70', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: params.toString(),
      });

      if (response.ok) {
        setFormStatus('success');
        setFormErrors({});
        setShowGeneralError(false);
        setSubmitAttempts(0);
        form.reset();

        // Scroll to top after 3 seconds and clear success message
        setTimeout(() => {
          scrollToTop();
          // Clear success message after scrolling
          setTimeout(() => {
            setFormStatus('');
          }, 500);
        }, 3000);
      } else {
        setFormStatus('error');
      }
    } catch (error) {
      setFormStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-white/80 backdrop-blur-lg border-b border-gray-200 z-50" role="navigation" aria-label="Main navigation">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <button
            onClick={handleHomeClick}
            className="font-bold text-xl hover:opacity-80 transition-all duration-300 cursor-pointer"
            aria-label="WebMadeEasy Home"
          >
            <h1 className="inline">WebMade<span className="text-purple-600">Easy</span></h1>
          </button>
          <div className="hidden md:flex items-center space-x-8">
            <a href="#portfolio" className="text-gray-600 hover:text-gray-900 transition-all duration-300">
              Portfolio
            </a>
            <a href="#process" className="text-gray-600 hover:text-gray-900 transition-all duration-300">
              Process
            </a>
            <a href="#testimonials" className="text-gray-600 hover:text-gray-900 transition-all duration-300">
              Reviews
            </a>
            <a href="#contact" className="text-gray-600 hover:text-gray-900 transition-all duration-300">
              Contact
            </a>
          </div>
          <a href="#contact" aria-label="Free consultation for website design">
            <button className="bg-purple-600 text-white px-3 sm:px-4 py-2 rounded-md text-sm sm:text-base font-semibold hover:bg-purple-700 transition-all duration-300 shadow-lg flex items-center gap-2 whitespace-nowrap">
              <span className="hidden sm:inline">Free Consultation</span>
              <span className="sm:hidden">Consult</span>
              <img
                src="https://pixel-perfect-sites-now.lovable.app/assets/us-flag-65Qk6CeA.png"
                alt="US Flag"
                className="w-5 h-3 sm:w-6 sm:h-4"
              />
            </button>
          </a>
        </div>
      </nav>

      {/* Hero Section */}
      <header className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-50 via-white to-purple-100"></div>
        <div className="absolute inset-0 opacity-10">
          <img
            src="https://pixel-perfect-sites-now.lovable.app/assets/hero-image-D8eECSdP.jpg"
            alt="Professional website design services for small businesses"
            className="w-full h-full object-cover object-top"
          />
        </div>
        <div className="relative z-10 max-w-6xl mx-auto px-6 text-center">
          <div className="animate-fade-in">
            <h2 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6 text-center">
              Your Business Deserves<br />
              <strong className="text-purple-600">Better</strong> Than an<br />
              Outdated Or No Website
            </h2>
            <p className="text-xl md:text-2xl text-gray-600 max-w-3xl mx-auto mb-8">
              Transform your <strong>small business</strong> with a <strong>professional website</strong> that converts visitors into customers. No tech
              skills required – we handle everything for your <strong>business website design</strong>.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <a href="#contact" aria-label="Start building your business website today">
                <button className="bg-purple-600 text-white px-6 sm:px-8 py-4 rounded-md text-base sm:text-lg font-semibold hover:bg-purple-700 transition-all duration-300 shadow-lg whitespace-nowrap w-full sm:w-auto">
                  Let&apos;s Build Your Online Presence Today
                </button>
              </a>
              <a href="#transformation" aria-label="View our website design portfolio">
                <button className="border-2 border-purple-600 text-purple-600 bg-transparent hover:bg-purple-600 hover:text-white px-6 sm:px-8 py-4 rounded-md text-base sm:text-lg font-semibold transition-all duration-300 whitespace-nowrap w-full sm:w-auto">
                  View Our Work
                </button>
              </a>
            </div>
          </div>
          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="text-4xl font-bold text-purple-600 mb-2">50+</div>
              <div className="text-gray-600">Websites Launched</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-purple-600 mb-2">98%</div>
              <div className="text-gray-600">Client Satisfaction</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-purple-600 mb-2">72hrs</div>
              <div className="text-gray-600">Average Turnaround</div>
            </div>
          </div>
        </div>
      </header>

      {/* Portfolio Section */}
      <section id="portfolio" className="py-24">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h3 className="text-4xl md:text-5xl font-bold mb-6">
              Our <strong className="text-purple-600">Portfolio</strong>
            </h3>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Every <strong>website design</strong> we create is mobile-responsive, fast-loading, and designed to convert visitors into
              customers for your <strong>small business</strong>.
            </p>
          </div>
          <div className="mb-16 rounded-2xl overflow-hidden shadow-lg">
            <img
              src="https://static.readdy.ai/image/a8745fa948dd53f2677be3216d866437/36894cd82e23fa5d5e3db45a0a4a0ce2.jpeg"
              alt="Portfolio showcase of responsive website designs for small businesses"
              title="Professional website design portfolio - mobile responsive websites"
              className="w-full h-auto"
            />
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <article className="bg-gradient-to-br from-white to-purple-50 p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 group cursor-pointer">
              <div className="w-full h-32 bg-purple-100 rounded-lg mb-4 flex items-center justify-center group-hover:bg-purple-200 transition-all duration-300">
                <i className="ri-restaurant-line text-4xl text-purple-600" aria-hidden="true"></i>
              </div>
              <div className="text-sm text-purple-600 font-semibold mb-2">Food & Beverage</div>
              <h4 className="font-bold text-lg mb-2">Local Restaurant</h4>
              <p className="text-gray-600 text-sm">Increased online orders by 300%</p>
            </article>
            <article className="bg-gradient-to-br from-white to-purple-50 p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 group cursor-pointer">
              <div className="w-full h-32 bg-purple-100 rounded-lg mb-4 flex items-center justify-center group-hover:bg-purple-200 transition-all duration-300">
                <i className="ri-heart-pulse-line text-4xl text-purple-600" aria-hidden="true"></i>
              </div>
              <div className="text-sm text-purple-600 font-semibold mb-2">Health & Wellness</div>
              <h4 className="font-bold text-lg mb-2">Fitness Studio</h4>
              <p className="text-gray-600 text-sm">Boosted class bookings by 250%</p>
            </article>
            <article className="bg-gradient-to-br from-white to-purple-50 p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 group cursor-pointer">
              <div className="w-full h-32 bg-purple-100 rounded-lg mb-4 flex items-center justify-center group-hover:bg-purple-200 transition-all duration-300">
                <i className="ri-scales-3-line text-4xl text-purple-600" aria-hidden="true"></i>
              </div>
              <div className="text-sm text-purple-600 font-semibold mb-2">Professional Services</div>
              <h4 className="font-bold text-lg mb-2">Legal Firm</h4>
              <p className="text-gray-600 text-sm">Generated 55+ new client inquiries</p>
            </article>
            <article className="bg-gradient-to-br from-white to-purple-50 p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 group cursor-pointer">
              <div className="w-full h-32 bg-purple-100 rounded-lg mb-4 flex items-center justify-center group-hover:bg-purple-200 transition-all duration-300">
                <i className="ri-shopping-bag-line text-4xl text-purple-600" aria-hidden="true"></i>
              </div>
              <div className="text-sm text-purple-600 font-semibold mb-2">E-commerce</div>
              <h4 className="font-bold text-lg mb-2">Retail Store</h4>
              <p className="text-gray-600 text-sm">Doubled online sales in 3 months</p>
            </article>
          </div>
        </div>
      </section>

      {/* Transformation Section */}
      <section id="transformation" className="py-24 bg-purple-50/50">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              See The <span className="text-purple-600">Transformation</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Don't let an outdated or missing website cost you customers. See how we transform businesses with modern,
              professional designs.
            </p>
          </div>
          
          {/* Mobile Headers */}
          <div className="md:hidden mb-6">
            <div className="grid grid-cols-2 gap-4 text-center">
              <div className="bg-red-500 text-white py-3 px-4 rounded-lg font-semibold">
                BEFORE: Losing Customers
              </div>
              <div className="bg-purple-600 text-white py-3 px-4 rounded-lg font-semibold">
                AFTER: Converting Visitors
              </div>
            </div>
          </div>
          
          <div className="relative shadow-lg rounded-2xl overflow-hidden">
            <img
              src="https://pixel-perfect-sites-now.lovable.app/assets/before-after-final-clean--CudjVa3.png"
              alt="Before and after website transformation"
              className="w-full h-auto"
            />
            {/* Desktop Overlay Labels */}
            <div className="hidden md:block absolute top-6 left-6 bg-red-500 text-white px-4 py-2 rounded-lg font-semibold">
              BEFORE: Losing Customers
            </div>
            <div className="hidden md:block absolute top-6 right-6 bg-purple-600 text-white px-4 py-2 rounded-lg font-semibold">
              AFTER: Converting Visitors
            </div>
          </div>
          <div className="mt-16 grid md:grid-cols-2 gap-12">
            <div className="bg-gradient-to-br from-white to-red-50 p-8 rounded-2xl shadow-lg">
              <h3 className="text-2xl font-bold mb-4 text-red-600">❌ What Hurts Your Business</h3>
              <ul className="space-y-3 text-gray-600">
                <li>• No online presence = invisible to customers</li>
                <li>• Outdated design = lack of credibility</li>
                <li>• Poor mobile experience = lost sales</li>
                <li>• Slow loading = frustrated visitors</li>
                <li>• No clear call-to-action = missed opportunities</li>
              </ul>
            </div>
            <div className="bg-gradient-to-br from-white to-purple-50 p-8 rounded-2xl shadow-lg">
              <h3 className="text-2xl font-bold mb-4 text-purple-600">✅ What We Deliver</h3>
              <ul className="space-y-3 text-gray-600">
                <li>• Professional design that builds trust</li>
                <li>• Mobile-first responsive layout</li>
                <li>• Lightning-fast loading speeds</li>
                <li>• Clear conversion paths</li>
                <li>• SEO-optimized for visibility</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section id="process" className="py-24 bg-purple-50/50">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h3 className="text-4xl md:text-5xl font-bold mb-6">
              Simple <strong className="text-purple-600">3-Step Process</strong>
            </h3>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Getting your dream <strong>website design</strong> is easier than you think. No technical knowledge required for your <strong>business website</strong>.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 relative">
            <div className="hidden md:block absolute top-16 left-1/3 right-1/3 h-0.5 bg-purple-200"></div>
            <div className="text-center relative">
              <div className="w-20 h-20 bg-purple-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-6 shadow-lg">
                01
              </div>
              <div className="text-6xl mb-6">🔍</div>
              <h3 className="text-2xl font-bold mb-4">Discovery</h3>
              <p className="text-gray-600 leading-relaxed">
                We learn about your business, goals, and target audience to create the perfect strategy.
              </p>
            </div>
            <div className="text-center relative">
              <div className="w-20 h-20 bg-purple-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-6 shadow-lg">
                02
              </div>
              <div className="text-6xl mb-6">🎨</div>
              <h3 className="text-2xl font-bold mb-4">Design</h3>
              <p className="text-gray-600 leading-relaxed">
                Our team crafts a stunning, custom design that reflects your brand and converts visitors.
              </p>
            </div>
            <div className="text-center relative">
              <div className="w-20 h-20 bg-purple-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-6 shadow-lg">
                03
              </div>
              <div className="text-6xl mb-6">🚀</div>
              <h3 className="text-2xl font-bold mb-4">Launch</h3>
              <p className="text-gray-600 leading-relaxed">
                We build, test, and launch your website, then provide ongoing support and training.
              </p>
            </div>
          </div>
          <div className="text-center mt-16">
            <div className="bg-gradient-to-br from-white to-purple-50 p-8 rounded-2xl shadow-lg max-w-2xl mx-auto">
              <h3 className="text-2xl font-bold mb-4">Ready to Get Started?</h3>
              <p className="text-gray-600 mb-6">
                Book a free 30-minute consultation to discuss your project. No obligation, just great advice.
              </p>
              <a href="#contact">
                <button className="bg-purple-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-purple-700 transition-all duration-300 shadow-lg whitespace-nowrap">
                  Schedule Free Consultation
                </button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-24">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h3 className="text-4xl md:text-5xl font-bold mb-6">
              What Our <strong className="text-purple-600">Clients Say</strong>
            </h3>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Join hundreds of <strong>small business owners</strong> who've transformed their online presence with our <strong>professional website design</strong>
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-gradient-to-br from-white to-purple-50 p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300">
              <div className="flex mb-4">
                <span className="text-purple-600 text-xl">★</span>
                <span className="text-purple-600 text-xl">★</span>
                <span className="text-purple-600 text-xl">★</span>
                <span className="text-purple-600 text-xl">★</span>
                <span className="text-purple-600 text-xl">★</span>
              </div>
              <blockquote className="text-lg mb-6">
                "Our new website has tripled our online orders! The design is beautiful and so easy for customers to
                use."
              </blockquote>
              <div className="flex items-center">
                <div className="w-12 h-12 bg-purple-200 rounded-full flex items-center justify-center text-purple-600 font-bold text-lg mr-4">
                  S
                </div>
                <div>
                  <div className="font-semibold">Sarah Johnson</div>
                  <div className="text-sm text-gray-600">Johnson&apos;s Bakery</div>
                </div>
              </div>
            </div>
            <div className="bg-gradient-to-br from-white to-purple-50 p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300">
              <div className="flex mb-4">
                <span className="text-purple-600 text-xl">★</span>
                <span className="text-purple-600 text-xl">★</span>
                <span className="text-purple-600 text-xl">★</span>
                <span className="text-purple-600 text-xl">★</span>
                <span className="text-purple-600 text-xl">★</span>
              </div>
              <blockquote className="text-lg mb-6">
                "Professional, fast, and affordable. My law firm finally has a website that matches our expertise."
              </blockquote>
              <div className="flex items-center">
                <div className="w-12 h-12 bg-purple-200 rounded-full flex items-center justify-center text-purple-600 font-bold text-lg mr-4">
                  M
                </div>
                <div>
                  <div className="font-semibold">Mike Rodriguez</div>
                  <div className="text-sm text-gray-600">Rodriguez Legal</div>
                </div>
              </div>
            </div>
            <div className="bg-gradient-to-br from-white to-purple-50 p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300">
              <div className="flex mb-4">
                <span className="text-purple-600 text-xl">★</span>
                <span className="text-purple-600 text-xl">★</span>
                <span className="text-purple-600 text-xl">★</span>
                <span className="text-purple-600 text-xl">★</span>
                <span className="text-purple-600 text-xl">★</span>
              </div>
              <blockquote className="text-lg mb-6">
                "The mobile design is perfect! Our class bookings have increased 200% since launching the new site."
              </blockquote>
              <div className="flex items-center">
                <div className="w-12 h-12 bg-purple-200 rounded-full flex items-center justify-center text-purple-600 font-bold text-lg mr-4">
                  L
                </div>
                <div>
                  <div className="font-semibold">Lisa Chen</div>
                  <div className="text-sm text-gray-600">Bloom Fitness Studio</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 bg-purple-50/50">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-16">
            <h3 className="text-4xl md:text-5xl font-bold mb-6">
              Ready To <strong className="text-purple-600">Transform</strong> Your Business?
            </h3>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Get a free consultation and custom quote for your <strong>website design</strong>. No pressure, just honest advice about what your <strong>small business</strong> needs.
            </p>
          </div>
          <div className="bg-gradient-to-br from-white to-purple-50 p-8 md:p-12 rounded-2xl shadow-lg">
            <form
              className="space-y-6"
              data-readdy-form
              id="consultation-form"
              onSubmit={handleSubmit}
            >
              {showGeneralError && Object.keys(formErrors).length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                  <div className="flex items-center">
                    <i className="ri-error-warning-line text-red-500 text-lg mr-2"></i>
                    <p className="text-red-700 text-sm font-medium">
                      Please fill in all required fields to continue.
                    </p>
                  </div>
                </div>
              )}
              {showGeneralError && Object.keys(formErrors).length === 0 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                  <div className="flex items-center">
                    <i className="ri-information-line text-yellow-600 text-lg mr-2"></i>
                    <p className="text-yellow-700 text-sm font-medium">
                      Please tell us about your project so we can better assist you, or click submit again to
                      continue without a message.
                    </p>
                  </div>
                </div>
              )}
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold mb-2">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    name="fullName"
                    required
                    className={`w-full px-4 py-3 rounded-lg border ${
                      formErrors.fullName ? 'border-red-500' : 'border-gray-300'
                    } bg-white focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500 transition-all duration-300`}
                    placeholder="Your full name"
                  />
                  {formErrors.fullName && (
                    <p className="text-red-500 text-sm mt-1">{formErrors.fullName}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2">
                    Email Address <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    className={`w-full px-4 py-3 rounded-lg border ${
                      formErrors.email ? 'border-red-500' : 'border-gray-300'
                    } bg-white focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500 transition-all duration-300`}
                    placeholder="your@email.com"
                  />
                  {formErrors.email && (
                    <p className="text-red-500 text-sm mt-1">{formErrors.email}</p>
                  )}
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold mb-2">Business Name</label>
                  <input
                    name="businessName"
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 bg-white focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500 transition-all duration-300"
                    placeholder="Your business name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2">Phone Number</label>
                  <input
                    type="tel"
                    name="phone"
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 bg-white focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500 transition-all duration-300"
                    placeholder="(555) 123-4567"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold mb-2">Tell us about your project</label>
                <textarea
                  rows={4}
                  name="message"
                  maxLength={500}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 bg-white focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500 transition-all duration-300 resize-none"
                  placeholder="Describe your business and what you're looking for in a website..."
                ></textarea>
              </div>
              <div className="text-center">
                <button
                  type="submit"
                  disabled={formStatus === 'submitting'}
                  className="bg-purple-600 text-white px-12 py-4 rounded-md text-lg font-semibold hover:bg-purple-700 transition-all duration-300 shadow-lg w-full md:w-auto whitespace-nowrap disabled:opacity-50 flex items-center justify-center mx-auto"
                >
                  {formStatus === 'submitting' ? 'Submitting...' : 'Get Your Free Consultation'}
                </button>
                {formStatus === 'success' && (
                  <p className="text-green-600 mt-4">Thank you! We'll contact you within 2 hours.</p>
                )}
                {formStatus === 'error' && (
                  <p className="text-red-600 mt-4">Something went wrong. Please try again.</p>
                )}
                <p className="text-sm text-gray-600 mt-4">
                  Usually responds within 2 hours • No spam, ever
                </p>
              </div>
            </form>
          </div>
          <div className="mt-16 grid md:grid-cols-3 gap-8 text-center">
            <a href="tel:+18478075850" className="block hover:scale-105 transition-transform cursor-pointer">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4 hover:bg-purple-200 transition-colors">
                <i className="ri-phone-line text-2xl text-purple-600"></i>
              </div>
              <h3 className="font-semibold mb-2">Call Us</h3>
              <p className="text-gray-600">‪(847) 807-5850‬</p>
            </a>
            <a href="mailto:hello@webmadeeasy.agency" className="block hover:scale-105 transition-transform cursor-pointer">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4 hover:bg-purple-200 transition-colors">
                <i className="ri-mail-line text-2xl text-purple-600"></i>
              </div>
              <h3 className="font-semibold mb-2">Email Us</h3>
              <p className="text-gray-600">hello@webmadeeasy.agency</p>
            </a>
            <button
              className="block hover:scale-105 transition-transform w-full cursor-pointer"
              onClick={() => document.querySelector('#vapi-widget-floating-button')?.click()}
            >
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4 hover:bg-purple-200 transition-colors">
                <i className="ri-chat-3-line text-2xl text-purple-600"></i>
              </div>
              <h3 className="font-semibold mb-2">Live Chat</h3>
              <p className="text-gray-600">Available 24/7</p>
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-16">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8">
            <div className="md:col-span-2">
              <div className="font-bold text-2xl mb-4">
                WebMade<span className="text-purple-400">Easy</span>
              </div>
              <p className="text-gray-400 mb-6">
                We help <strong>small businesses</strong> create <strong>professional websites</strong> that convert visitors into customers. No tech
                skills required for your <strong>business website design</strong>.
              </p>
            </div>
            <nav aria-label="Services">
              <h4 className="font-semibold mb-4">Services</h4>
              <ul className="space-y-2 text-gray-400">
                <li>
                  <a href="#contact" className="hover:text-white transition-all duration-300 cursor-pointer">
                    Website Design
                  </a>
                </li>
                <li>
                  <a href="#contact" className="hover:text-white transition-all duration-300 cursor-pointer">
                    Mobile Optimization
                  </a>
                </li>
                <li>
                  <a href="#contact" className="hover:text-white transition-all duration-300 cursor-pointer">
                    SEO Setup
                  </a>
                </li>
                <li>
                  <a href="#contact" className="hover:text-white transition-all duration-30 cursor-pointer">
                    Maintenance
                  </a>
                </li>
              </ul>
            </nav>
            <div>
              <h4 className="font-semibold mb-4">Contact</h4>
              <address className="space-y-2 text-gray-400 not-italic">
                <div>‪(847) 807-5850‬</div>
                <div>Glenview, IL 60025</div>
                <div>Free consultations available</div>
              </address>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-12 pt-8 text-center text-gray-500">
            <p>© 2026 WebMadeEasy. All rights reserved. | Built with care for small businesses.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
